import { useEffect, useRef } from "react";
import { AppState, AppStateStatus } from "react-native";
import { useAppSelector } from "../redux/store";
import { listenToDocuments, QueryOptions } from "../services/firestoreServices";
import { setBadgeCount } from "../services/notifications/badgeService";
import { Screen, WhereCondition } from "../config/companies/config";
import { getValue } from "../screens/list/ListScreen";

const STATUS_OPEN = "open";

export default function useTaskBadge() {
  const auth = useAppSelector((state) => state.auth);
  const config = auth.config;
  const user = auth.user;
  const lastCountRef = useRef(0);

  const role = user?.role;
  const homeScreenName =
    (role && config.initialScreenByRole?.[role]) ?? config.initialScreen;

  const homeScreen: Screen | undefined = homeScreenName
    ? config.screens?.find((s) => s.name === homeScreenName)
    : undefined;

  const collection = homeScreen
    ? config.database?.find((c) => c.name === homeScreen.collection)
    : undefined;

  const hasStatusField = !!collection?.fields?.some((f) => f.name === "status");

  useEffect(() => {
    if (!user?.uid) return;

    if (!homeScreen || !hasStatusField) {
      lastCountRef.current = 0;
      void setBadgeCount(0);
      return;
    }

    const resolvedWhere: WhereCondition[] = (homeScreen.where ?? []).reduce(
      (acc: WhereCondition[], item) => {
        if (item.applyToRoles && !item.applyToRoles.includes(role)) {
          return acc;
        }
        const filterValue = item.from
          ? getValue(
              (auth as Record<string, any>)[item.from],
              String(item.value),
            )
          : item.value;
        acc.push({ ...item, value: filterValue });
        return acc;
      },
      [],
    );

    resolvedWhere.push({
      field: "status",
      operator: "==",
      value: STATUS_OPEN,
    });

    const options: QueryOptions = {
      where: resolvedWhere,
      onlyCompany: homeScreen.onlyCompany ?? true,
    };

    const unsubscribe = listenToDocuments(
      homeScreen.collection,
      (data) => {
        const count = Array.isArray(data) ? data.length : 0;
        lastCountRef.current = count;
        void setBadgeCount(count);
      },
      options,
    );

    return () => {
      unsubscribe?.();
    };
  }, [user?.uid, role, homeScreen, hasStatusField, auth]);

  useEffect(() => {
    const handleAppStateChange = (next: AppStateStatus) => {
      if (next === "active") {
        void setBadgeCount(lastCountRef.current);
      }
    };
    const sub = AppState.addEventListener("change", handleAppStateChange);
    return () => sub.remove();
  }, []);
}
